import arrayLength from "@-/com.npmjs.extra-array__length";
import runes from "@-/com.npmjs.runes2";

import { alignUtterance } from "#align-utterance";
import type { ProducedToken, SentenceDrillToken } from "#sentence-drill-token";
import {
  createSentenceDrillState,
  deriveCursorIndex,
  type SentenceDrillMode,
  type SentenceDrillState,
  type SentenceDrillTokenStatus,
} from "#sentence-drill-state";

export type SentenceDrillAction =
  /** The user produced something — a spoken utterance or a typed run. */
  | { kind: "attempt"; produced: readonly ProducedToken[] }
  /**
   * The user ASKED for help on the current blank.
   *
   * Distinct from a failed attempt on purpose: most wrong answers in this drill are the user
   * offering a perfectly good word that simply is not the one this sentence uses, which is a
   * disambiguation problem rather than a knowledge gap. Only an explicit request — or an attempt
   * that demonstrably ran into the wrong word — moves the hint ladder.
   */
  | { kind: "request-hint" }
  /**
   * Start over on a new token list.
   *
   * The tokens arrive asynchronously (jieba runs in a query), so a drill is necessarily created
   * before its sentence is known and must be re-seeded when it resolves. Also what advances the
   * drill to the next card without remounting.
   */
  | { kind: "reset"; mode: SentenceDrillMode; tokens: readonly SentenceDrillToken[] }
  /**
   * The user deliberately submitted a WRONG answer for the current blank.
   *
   * Distinct from an `attempt` that happens not to match. An unmatched attempt is ambiguous — in
   * speech mode it is usually recognizer noise, and escalating on it would spend a hint every time
   * the mic mishears — so `attempt` deliberately does nothing when nothing aligns. Typed input has
   * no such ambiguity: the user chose those syllables and submitted them, so it earns a hint.
   */
  | { kind: "wrong-answer" }
  /** The user gave up on this blank: reveal it, rate it, and end the drill. */
  | { kind: "reveal" };

/** Character count of a token's word, counted by grapheme cluster rather than UTF-16 unit. */
function wordLength(word: string): number {
  return arrayLength(runes(word));
}

/**
 * Advance the hint for one token.
 *
 * The ladder, per the drill's design:
 *   * `pending` → `hinted` showing ONE character's worth of help.
 *   * `hinted` on a MULTI-character word → uncover one more leading character. A long word should
 *     give ground gradually; revealing all of it at the first stumble skips the learning.
 *   * `hinted` on a SINGLE-character word (or a multi-character word already down to its last
 *     hidden character) → `revealed`: show everything, bold it, and mark the word for an Again
 *     rating. There is nothing left to hint with, and the user has now been told the answer.
 */
function escalateStatus(status: SentenceDrillTokenStatus, word: string): SentenceDrillTokenStatus {
  const length = wordLength(word);
  if (status.kind === "pending") {
    // A single-character word has nothing to partially uncover: one character of help IS the whole
    // word, so a first hint on it goes straight to the bolded reveal.
    return length <= 1 ? { kind: "revealed" } : { kind: "hinted", revealedCharacterCount: 1 };
  }
  if (status.kind === "hinted") {
    const next = status.revealedCharacterCount + 1;
    return next >= length ? { kind: "revealed" } : { kind: "hinted", revealedCharacterCount: next };
  }
  return status;
}

function withStatusAt(
  statusList: readonly SentenceDrillTokenStatus[],
  index: number,
  status: SentenceDrillTokenStatus,
): SentenceDrillTokenStatus[] {
  const next = [...statusList];
  next[index] = status;
  return next;
}

function settle(
  state: SentenceDrillState,
  statusList: readonly SentenceDrillTokenStatus[],
  overrides: Partial<Pick<SentenceDrillState, "isFinished">> = {},
): SentenceDrillState {
  return {
    ...state,
    ...overrides,
    cursorIndex: deriveCursorIndex({ statusList, tokens: state.tokens }),
    statusList,
  };
}

/**
 * The drill's whole transition function.
 *
 * Pure and free of React, the database, and jieba: the tokens arrive already segmented and the
 * utterance already tokenized, so every rule below is directly testable without a rendered tree or
 * a seeded DB.
 */
export function reduceSentenceDrill(
  state: SentenceDrillState,
  action: SentenceDrillAction,
): SentenceDrillState {
  // Checked BEFORE the finished guard: a reset is how the next card (or the just-resolved
  // segmentation) arrives, and a finished drill must still accept it or the drill would be stuck on
  // the rating prompt forever.
  if (action.kind === "reset") {
    return createSentenceDrillState({ mode: action.mode, tokens: action.tokens });
  }

  // A finished drill is inert. The user is looking at a rating prompt; late speech-recognition
  // results are still arriving at this point and must not mutate what they are being asked about.
  if (state.isFinished) {
    return state;
  }

  switch (action.kind) {
    case "attempt": {
      const alignment = alignUtterance({
        cursorIndex: state.cursorIndex,
        produced: action.produced,
        tokens: state.tokens,
      });
      // Nothing they said matched anywhere. Not a failure at the cursor — the recognizer may simply
      // have returned noise — so the hint ladder does not move.
      if (alignment === null) {
        return state;
      }

      let statusList = [...state.statusList];
      for (const index of alignment.matchedIndexList) {
        const current = statusList[index];
        // A `revealed` token stays revealed even if later spoken correctly: it was given away, and
        // its Again rating reflects that. Anything else becomes `filled`, so a token the user
        // eventually got right after hints still scores as correct.
        if (current !== undefined && current.kind !== "revealed") {
          statusList[index] = { kind: "filled" };
        }
      }

      const nextState = settle(state, statusList);
      if (!alignment.endedOnMismatch) {
        return nextState;
      }

      // They kept going and hit a word they don't have. That is a genuine miss at the new cursor,
      // so it earns a hint.
      const blockedIndex = nextState.cursorIndex;
      const blockedToken = state.tokens[blockedIndex];
      const blockedStatus = statusList[blockedIndex];
      if (blockedToken === undefined || blockedStatus === undefined) {
        return nextState;
      }
      statusList = withStatusAt(
        statusList,
        blockedIndex,
        escalateStatus(blockedStatus, blockedToken.word),
      );
      return settle(state, statusList);
    }

    // Both escalate the cursor token by one step; they are separate actions only so the CALLER
    // states which happened, since an unmatched `attempt` cannot tell them apart on its own.
    case "request-hint":
    case "wrong-answer": {
      const index = state.cursorIndex;
      const token = state.tokens[index];
      const status = state.statusList[index];
      if (token === undefined || status === undefined) {
        return state;
      }
      return settle(
        state,
        withStatusAt(state.statusList, index, escalateStatus(status, token.word)),
      );
    }

    case "reveal": {
      const index = state.cursorIndex;
      const status = state.statusList[index];
      // Reveal with nothing left to reveal (the sentence was completed) just ends the drill.
      if (status === undefined) {
        return { ...state, isFinished: true };
      }
      return settle(state, withStatusAt(state.statusList, index, { kind: "revealed" }), {
        isFinished: true,
      });
    }
  }
}
